import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Share2, 
  Copy, 
  Check,
  ExternalLink,
  Tag,
  Users,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Project } from '../lib/supabase';
import SEO from '../components/SEO';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) {
        navigate('/verkefni');
        return;
      }
      setProject(data);
    } catch (error) {
      console.error('Error fetching project:', error);
      navigate('/verkefni');
    } finally {
      setLoading(false);
    }
  };

  const shareProject = async () => {
    if (!project) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: project.title,
          text: project.description,
          url: window.location.href,
        });
      } catch (error) {
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EA295A] mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <>
      <SEO 
        title={project.title}
        description={project.description}
        image={project.image_url}
      />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back button */}
          <Link 
            to="/verkefni"
            className="inline-flex items-center text-gray-600 hover:text-[#EA295A] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Til baka í verkefnalista
          </Link>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                {project.image_url && (
                  <div className="relative aspect-video">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="p-8">
                  <div className="flex items-center justify-end mb-6">
                    <motion.button
                      onClick={shareProject}
                      className="flex items-center gap-2 px-3 py-1.5 text-gray-500 hover:text-[#EA295A] transition-colors rounded-lg hover:bg-gray-50"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {copied ? (
                        <>
                          <Check className="w-5 h-5" />
                          <span className="text-sm">Afritað</span>
                        </>
                      ) : (
                        <>
                          {navigator.share ? (
                            <Share2 className="w-5 h-5" />
                          ) : (
                            <Copy className="w-5 h-5" />
                          )}
                          <span className="text-sm">Deila</span>
                        </>
                      )}
                    </motion.button>
                  </div>

                  <h1 className="text-3xl font-bold text-gray-900 mb-6">
                    {project.title}
                  </h1>

                  <div className="prose max-w-none">
                    <p className="text-xl text-gray-600 mb-8">
                      {project.description}
                    </p>

                    {project.content && project.content.split('\n').map((paragraph, index) => (
                      <p key={index} className="text-gray-600 mb-4">
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {/* Project Features */}
                  <div className="mt-12 pt-8 border-t border-gray-100">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">
                      Helstu eiginleikar
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-3 mt-1" />
                        <div>
                          <h3 className="font-medium text-gray-900">Responsive hönnun</h3>
                          <p className="text-sm text-gray-600">Virkar á öllum tækjum</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-3 mt-1" />
                        <div>
                          <h3 className="font-medium text-gray-900">SEO vænt</h3>
                          <p className="text-sm text-gray-600">Hámarkar sýnileika</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-3 mt-1" />
                        <div>
                          <h3 className="font-medium text-gray-900">Hraðvirkt</h3>
                          <p className="text-sm text-gray-600">Hámarks afköst</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-3 mt-1" />
                        <div>
                          <h3 className="font-medium text-gray-900">Öruggt</h3>
                          <p className="text-sm text-gray-600">SSL dulkóðun</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              className="lg:col-span-1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm sticky top-32">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-3">Flokkur</h3>
                    <div className="flex items-center text-gray-900">
                      <Tag className="w-5 h-5 text-[#EA295A] mr-2" />
                      {project.category}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-3">Tækni</h3>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-3">Teymi</h3>
                    <div className="flex items-center text-gray-900">
                      <Users className="w-5 h-5 text-[#EA295A] mr-2" />
                      Vefsíða Team
                    </div>
                  </div>

                  {project.link && (
                    <div className="pt-6 mt-6 border-t border-gray-100">
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full bg-[#EA295A] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#d01d4c] transition-all"
                      >
                        Skoða verkefni
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </a>
                    </div>
                  )}

                  <div className="pt-6 mt-6 border-t border-gray-100">
                    <Link
                      to="/fa-tilbod"
                      className="flex items-center justify-center w-full bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-all"
                    >
                      Fá tilboð í svipað verkefni
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectDetails;